import { Activity, P } from '@fashionnovateam/nova-ui-toolkit';
import {
  type ReorderWorksheetActivity,
  type UpdateFormEventParams,
} from '../../../../../Lib/Api/types/reorder-chat';

type UpdateFormActivityProps = {
  activity: ReorderWorksheetActivity;
  isLast: boolean;
};

type FieldRow = {
  field: string;
  label: string;
  formattedNew: string;
  isUpdated: boolean;
};

const DISPLAY_FIELDS: readonly { key: string; label: string }[] = [
  { key: 'cost_jr', label: 'Cost JR' },
  { key: 'cost_plus', label: 'Cost Plus' },
  { key: 'country_of_origin', label: 'COO' },
  { key: 'earliest_custom_cut_delivery_date', label: 'E. CC Delivery' },
  { key: 'custom_cut_lead_time_in_weeks', label: 'CC Lead Time' },
  { key: 'shipment_mode', label: 'Shipment Mode' },
  { key: 'stock_quantity', label: 'Stk Qty' },
  { key: 'stock_ratio', label: 'Stk Ratio' },
  { key: 'earliest_IHD_date_to_deliver_stock', label: 'E. IHD Date' },
  { key: 'incoming_stock_quantity', label: 'Inc. Stock Qty' },
  { key: 'incoming_stock_date', label: 'Inc. Stock Date' },
  { key: 'incoming_stock_ratio', label: 'Inc. Stock Ratio' },
  { key: 'custom_MOQ_jr', label: 'Cust. MOQ Jr' },
  { key: 'custom_MOQ_plus', label: 'Cust. MOQ Plus' },
];

const CURRENCY_FIELDS = new Set(['cost_jr', 'cost_plus']);
const WEEKS_FIELDS = new Set(['custom_cut_lead_time_in_weeks']);
const DATE_FIELDS = new Set([
  'earliest_IHD_date_to_deliver_stock',
  'incoming_stock_date',
  'earliest_custom_cut_delivery_date',
]);

function formatFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') {
    if (DATE_FIELDS.has(field) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        const d = new Date(value);
        if (!Number.isNaN(d.getTime()))
          return d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC',
          });
      } catch {
        // fall through to return value
      }
    }
    return value;
  }
  if (typeof value === 'number') {
    if (CURRENCY_FIELDS.has(field)) return `$${value.toFixed(2)}`;
    if (WEEKS_FIELDS.has(field))
      return `${value} ${value === 1 ? 'week' : 'weeks'}`;
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) {
    return value.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  return String(value);
}

function buildChangeRows(params: UpdateFormEventParams): FieldRow[] {
  const { changes } = params;
  if (!changes) return [];

  const newObj = changes.new ?? {};
  const oldObj = changes.old ?? {};

  return DISPLAY_FIELDS.map(({ key, label }) => {
    const newVal = newObj[key];
    const oldVal = oldObj[key];
    const formattedNew = formatFieldValue(key, newVal);
    const formattedOld = formatFieldValue(key, oldVal);
    // Only mark as updated when the key exists in new and (missing in old or value changed)
    const isUpdated =
      key in newObj && (!(key in oldObj) || formattedOld !== formattedNew);

    return {
      field: key,
      label,
      formattedNew,
      isUpdated,
    };
  });
}

export function UpdateFormActivity({
  activity,
  isLast,
}: UpdateFormActivityProps) {
  const { author, created_at: createdAt, event_params: eventParams } = activity;
  const params = eventParams as UpdateFormEventParams;
  const displayName = author.name;

  const badgeType = 'vendor';

  const rows = buildChangeRows(params);

  const mainContent = (
    <>
      <span className="nu-font-normal"> updated </span>
      <span className="nu-font-semibold nu-text-base-primary">
        Item Details
      </span>
    </>
  );

  const midpoint = Math.ceil(rows.length / 2);
  const leftColumn = rows.slice(0, midpoint);
  const rightColumn = rows.slice(midpoint);

  const detailContent = (
    <div className="flex gap-4 p-1">
      <div className="flex flex-col gap-1 min-w-[140px]">
        {leftColumn.map((row) => (
          <div key={row.field} className="flex gap-2 items-baseline">
            <P size="2xs" color="disabled" className="shrink-0 w-[100px]">
              {row.label}
            </P>
            <P size="2xs" color={row.isUpdated ? 'warning' : 'primary'}>
              {row.formattedNew}
            </P>
          </div>
        ))}
      </div>
      {rightColumn.length > 0 && (
        <div className="flex flex-col gap-1 min-w-[140px]">
          {rightColumn.map((row) => (
            <div key={row.field} className="flex gap-2 items-baseline">
              <P size="2xs" color="disabled" className="shrink-0 w-[100px]">
                {row.label}
              </P>
              <P size="2xs" color={row.isUpdated ? 'warning' : 'primary'}>
                {row.formattedNew}
              </P>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Activity
      name={displayName}
      badgeType={badgeType}
      hideBar={isLast}
      className="w-full !max-w-full"
    >
      <Activity.EventDetailed
        name={displayName}
        date={createdAt}
        text={mainContent}
        className="w-full"
        tooltipZIndex={100}
      >
        {rows.length > 0 ? detailContent : null}
      </Activity.EventDetailed>
    </Activity>
  );
}
